import { type EffectCallback, type MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCognitoAnyUserState } from '../reducers/cognitoUserReducer';
import { isSome } from '../utils';

export function useDidMountEffect(effect: EffectCallback) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(effect, []);
}

export { usePrev } from '@eflight/shared';

export function useCognitoUser() {
    const cognitoUser = useCognitoAnyUserState();

    if (cognitoUser !== 'processing' && cognitoUser !== 'signed-out') {
        return cognitoUser;
    }
    return undefined;
}

export function useStateRef<S>(initialState: S) {
    const [innerState, setInnerState] = useState(initialState);
    const stateRef = useRef(innerState);

    useEffect(() => {
        stateRef.current = innerState;
    }, [innerState]);

    const setState = useCallback((setStateAction: React.SetStateAction<S>) => {
        setInnerState((prevState) => {
            const nextState = isStateActionFunction(setStateAction) ? setStateAction(prevState) : setStateAction;
            stateRef.current = nextState;
            return nextState;
        });
    }, []);

    // We dont want staterRef to be mutable, because change would not be reflected in state,
    // so we cast it to Readonly
    return [innerState, setState, stateRef as Readonly<MutableRefObject<S>>] as const;
}

function isStateActionFunction<S>(state: React.SetStateAction<S>): state is (prevState: S) => S {
    return typeof state === 'function';
}

export function usePropRef<S>(propValue: S) {
    const ref = useRef(propValue);

    useEffect(() => {
        ref.current = propValue;
    }, [propValue]);

    return ref as Readonly<MutableRefObject<S>>;
}

export function usePropLocalState<T>(propValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [localValue, setLocalValue] = useState(propValue);

    useEffect(() => {
        setLocalValue(propValue);
    }, [propValue]);

    return [localValue, setLocalValue];
}

type LocalStorageKeys = 'scheduling_calendar_minItemResolution' | 'scheduling_custom_filter';

export function useLocalStorageState<T extends string>(key: LocalStorageKeys) {
    const [value, setValue] = useState<T | undefined>((window.localStorage.getItem(key) as T) ?? undefined);
    const setLocalStorageValue = (newValue: T | undefined) => {
        setValue(newValue);
        if (isSome(newValue)) {
            window.localStorage.setItem(key, newValue);
        } else {
            window.localStorage.removeItem(key);
        }
    };
    return [value, setLocalStorageValue] as const;
}
